/**
 * Wire-контракт импорта накладных (`business-data-v1.schema.json`) и его
 * проверка на границе. Перечисления приходят строками (имя члена), числа — JSON-числами.
 */
import {
  asBoolean,
  asNumber,
  asObject,
  asOneOf,
  asString,
  field,
  mapArray,
  optional,
  type Raw,
} from '../../pricing/api/decode'
import {
  INVOICE_COLUMN_ROLES,
  ISSUE_SEVERITIES,
  PROFILE_MATCHED_BY,
  type ColumnMapping,
  type DetectedColumn,
  type ImportProfile,
  type ImportedFile,
  type InvoiceColumnRole,
  type MappingSnapshot,
  type ParsedInvoice,
  type ParsedInvoiceRow,
  type ProfileColumn,
  type ValidationIssue,
  type ValidationReport,
  type Worksheet,
} from '../model/types'

const text = (raw: Raw, name: string, path: string) => asString(field(raw, name, path), `${path}.${name}`)
const num = (raw: Raw, name: string, path: string) => asNumber(field(raw, name, path), `${path}.${name}`)
const flag = (raw: Raw, name: string, path: string) => asBoolean(field(raw, name, path), `${path}.${name}`)
const role = (value: unknown, path: string): InvoiceColumnRole => asOneOf(value, path, INVOICE_COLUMN_ROLES)
const optText = (raw: Raw, name: string, path: string) => optional(raw[name], `${path}.${name}`, asString)
const optNum = (raw: Raw, name: string, path: string) => optional(raw[name], `${path}.${name}`, asNumber)
const optInt = optNum

function decodeProfileColumn(raw: unknown, path: string): ProfileColumn {
  const item = asObject(raw, path)
  return {
    role: role(field(item, 'role', path), `${path}.role`),
    headerText: optText(item, 'headerText', path),
    columnIndex: optInt(item, 'columnIndex', path),
    isRequiredOverride: optional(item.isRequiredOverride, `${path}.isRequiredOverride`, asBoolean),
  }
}

export function decodeProfile(raw: unknown, path: string): ImportProfile {
  const item = asObject(raw, path)
  return {
    id: text(item, 'id', path),
    name: text(item, 'name', path),
    supplierName: optText(item, 'supplierName', path),
    headerFingerprint: optText(item, 'headerFingerprint', path),
    sheetNameHint: optText(item, 'sheetNameHint', path),
    headerRowIndex: optInt(item, 'headerRowIndex', path),
    defaultUnit: optText(item, 'defaultUnit', path),
    defaultVatRate: optText(item, 'defaultVatRate', path),
    priceIncludesVat: flag(item, 'priceIncludesVat', path),
    quantityInPackages: flag(item, 'quantityInPackages', path),
    isActive: flag(item, 'isActive', path),
    createdAt: text(item, 'createdAt', path),
    updatedAt: text(item, 'updatedAt', path),
    createdBy: text(item, 'createdBy', path),
    usageCount: num(item, 'usageCount', path),
    lastUsedAt: optText(item, 'lastUsedAt', path),
    columns: mapArray(field(item, 'columns', path), `${path}.columns`, decodeProfileColumn),
  }
}

export const decodeProfiles = (raw: unknown, path: string) => mapArray(raw, path, decodeProfile)

function decodeSnapshot(raw: unknown, path: string): MappingSnapshot {
  const item = asObject(raw, path)
  return {
    headerRowIndex: optInt(item, 'headerRowIndex', path),
    matchedBy: asOneOf(field(item, 'matchedBy', path), `${path}.matchedBy`, PROFILE_MATCHED_BY),
    profileId: optText(item, 'profileId', path),
    profileName: optText(item, 'profileName', path),
    columns: mapArray(field(item, 'columns', path), `${path}.columns`, (column, columnPath) => {
      const entry = asObject(column, columnPath)
      return {
        columnIndex: num(entry, 'columnIndex', columnPath),
        headerText: optText(entry, 'headerText', columnPath),
        role: role(field(entry, 'role', columnPath), `${columnPath}.role`),
      }
    }),
  }
}

export function decodeFile(raw: unknown, path: string): ImportedFile {
  const item = asObject(raw, path)
  return {
    id: text(item, 'id', path),
    fileName: text(item, 'fileName', path),
    contentType: text(item, 'contentType', path),
    sizeBytes: num(item, 'sizeBytes', path),
    sha256: text(item, 'sha256', path),
    uploadedAt: text(item, 'uploadedAt', path),
    uploadedBy: text(item, 'uploadedBy', path),
    sheetName: optText(item, 'sheetName', path),
    headerRowIndex: optInt(item, 'headerRowIndex', path),
    parserVersion: text(item, 'parserVersion', path),
    supplyId: optText(item, 'supplyId', path),
    importProfileId: optText(item, 'importProfileId', path),
    effectiveMapping: optional(item.effectiveMapping, `${path}.effectiveMapping`, decodeSnapshot),
  }
}

function decodeIssue(raw: unknown, path: string): ValidationIssue {
  const item = asObject(raw, path)
  return {
    rowNumber: optInt(item, 'rowNumber', path),
    role: optional(item.role, `${path}.role`, role),
    columnIndex: optInt(item, 'columnIndex', path),
    code: text(item, 'code', path),
    severity: asOneOf(field(item, 'severity', path), `${path}.severity`, ISSUE_SEVERITIES),
    message: text(item, 'message', path),
    rawValue: optText(item, 'rawValue', path),
    suggestedValue: optText(item, 'suggestedValue', path),
    relatedRows: optional(item.relatedRows, `${path}.relatedRows`, (rows, rowsPath) =>
      mapArray(rows, rowsPath, (row, rowPath) => asNumber(row, rowPath)),
    ),
  }
}

export function decodeReport(raw: unknown, path: string): ValidationReport {
  const item = asObject(raw, path)
  return {
    fileId: text(item, 'fileId', path),
    isBlocked: flag(item, 'isBlocked', path),
    errorCount: num(item, 'errorCount', path),
    warningCount: num(item, 'warningCount', path),
    infoCount: num(item, 'infoCount', path),
    rowsTotal: num(item, 'rowsTotal', path),
    rowsValid: num(item, 'rowsValid', path),
    rowsWithErrors: num(item, 'rowsWithErrors', path),
    rowsWithWarnings: num(item, 'rowsWithWarnings', path),
    declaredTotal: optNum(item, 'declaredTotal', path),
    computedTotal: num(item, 'computedTotal', path),
    isTruncated: flag(item, 'isTruncated', path),
    validatedAt: text(item, 'validatedAt', path),
    issues: mapArray(field(item, 'issues', path), `${path}.issues`, decodeIssue),
  }
}

function decodeDetected(raw: unknown, path: string): DetectedColumn {
  const item = asObject(raw, path)
  return {
    columnIndex: num(item, 'columnIndex', path),
    headerText: optText(item, 'headerText', path),
    suggestedRole: role(field(item, 'suggestedRole', path), `${path}.suggestedRole`),
    score: optNum(item, 'score', path),
    isApplied: flag(item, 'isApplied', path),
  }
}

function decodeColumnMapping(raw: unknown, path: string): ColumnMapping {
  const item = asObject(raw, path)
  return {
    headerRowIndex: optInt(item, 'headerRowIndex', path),
    matchedBy: asOneOf(field(item, 'matchedBy', path), `${path}.matchedBy`, PROFILE_MATCHED_BY),
    profileId: optText(item, 'profileId', path),
    profileName: optText(item, 'profileName', path),
    columns: mapArray(field(item, 'columns', path), `${path}.columns`, decodeDetected),
    missingRequiredRoles: mapArray(field(item, 'missingRequiredRoles', path), `${path}.missingRequiredRoles`, role),
  }
}

function decodeRow(raw: unknown, path: string): ParsedInvoiceRow {
  const item = asObject(raw, path)
  return {
    sourceRowNumber: num(item, 'sourceRowNumber', path),
    name: optText(item, 'name', path),
    sku: optText(item, 'sku', path),
    barcode: optText(item, 'barcode', path),
    quantity: optNum(item, 'quantity', path),
    unit: optText(item, 'unit', path),
    purchasePrice: optNum(item, 'purchasePrice', path),
    sum: optNum(item, 'sum', path),
    vatRate: optText(item, 'vatRate', path),
    vatAmount: optNum(item, 'vatAmount', path),
    notes: mapArray(field(item, 'notes', path), `${path}.notes`, asString),
  }
}

function decodeWorksheet(raw: unknown, path: string): Worksheet {
  const item = asObject(raw, path)
  return {
    name: text(item, 'name', path),
    position: num(item, 'position', path),
    isVisible: flag(item, 'isVisible', path),
    rowCount: num(item, 'rowCount', path),
    columnCount: num(item, 'columnCount', path),
  }
}

export function decodeParsedInvoice(raw: unknown, path: string): ParsedInvoice {
  const item = asObject(raw, path)
  return {
    fileId: text(item, 'fileId', path),
    fileName: text(item, 'fileName', path),
    sheetName: text(item, 'sheetName', path),
    headerRowIndex: optInt(item, 'headerRowIndex', path),
    mapping: decodeColumnMapping(field(item, 'mapping', path), `${path}.mapping`),
    rows: mapArray(field(item, 'rows', path), `${path}.rows`, decodeRow),
    worksheets: mapArray(field(item, 'worksheets', path), `${path}.worksheets`, decodeWorksheet),
    declaredTotal: optNum(item, 'declaredTotal', path),
    notes: mapArray(field(item, 'notes', path), `${path}.notes`, asString),
    validation: optional(item.validation, `${path}.validation`, decodeReport),
  }
}
